/**
 * scope.ts — the product and channel (or network) a person is working on in Ads and Social.
 *
 * They live in the address (?product=&channel= for Ads, ?product=&network= for Social) so a link or a refresh keeps
 * them, and the last choice is remembered on this device. The address wins over the remembered choice. The scope bar in
 * the shell is the only place that changes them; pages just read them.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { AD_CHANNELS, AD_PRODUCT_IDS, SOCIAL_NETWORK_CHOICES, type AdChannel, type AdProductId } from "@demandhub-cms/shared";

export type SocialScopeNetwork = "all" | (typeof SOCIAL_NETWORK_CHOICES)[number]["value"];
export const SOCIAL_SCOPE_NETWORKS: readonly SocialScopeNetwork[] = ["all", ...SOCIAL_NETWORK_CHOICES.map((n) => n.value)];

const STORE_KEY = "cms.scope.v2";
export const DEFAULT_PRODUCT: AdProductId = "sonic_hire";
export const DEFAULT_CHANNEL: AdChannel = "linkedin";
export const DEFAULT_NETWORK: SocialScopeNetwork = "all";

const asProduct = (v: unknown): AdProductId | null => ((AD_PRODUCT_IDS as readonly string[]).includes(v as string) ? (v as AdProductId) : null);
const asChannel = (v: unknown): AdChannel | null => ((AD_CHANNELS as readonly string[]).includes(v as string) ? (v as AdChannel) : null);
const asNetwork = (v: unknown): SocialScopeNetwork | null => ((SOCIAL_SCOPE_NETWORKS as readonly string[]).includes(v as string) ? (v as SocialScopeNetwork) : null);

interface Stored {
  product: AdProductId | null;
  channel: AdChannel | null;
  network: SocialScopeNetwork | null;
}

function read(): Stored {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) ?? "null") as Record<string, unknown> | null;
    return { product: asProduct(raw?.product), channel: asChannel(raw?.channel), network: asNetwork(raw?.network) };
  } catch {
    return { product: null, channel: null, network: null };
  }
}

function write(next: { product: AdProductId; channel: AdChannel; network: SocialScopeNetwork }) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  } catch {
    /* Storage can be blocked. The address still holds the choice. */
  }
}

/** True on the routes that carry a scope: every Ads and Social page. */
export const isScopedPath = (pathname: string): boolean => pathname === "/ads" || pathname.startsWith("/ads/") || pathname === "/social" || pathname.startsWith("/social/");
export const isSocialPath = (pathname: string): boolean => pathname === "/social" || pathname.startsWith("/social/");

export interface Scope {
  product: AdProductId;
  channel: AdChannel;
  network: SocialScopeNetwork;
  setProduct: (p: AdProductId) => void;
  setChannel: (c: AdChannel) => void;
  setNetwork: (n: SocialScopeNetwork) => void;
  /** "?product=…&channel=…" (or &network=…) for a link that should keep the scope. */
  search: string;
}

export function useScope(): Scope {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const social = isSocialPath(pathname);
  const stored = useMemo(read, []);
  const product = asProduct(params.get("product")) ?? stored.product ?? DEFAULT_PRODUCT;
  const channel = asChannel(params.get("channel")) ?? stored.channel ?? DEFAULT_CHANNEL;
  const network = asNetwork(params.get("network")) ?? stored.network ?? DEFAULT_NETWORK;

  const apply = useCallback(
    (patch: Partial<{ product: AdProductId; channel: AdChannel; network: SocialScopeNetwork }>) => {
      const next = { product, channel, network, ...patch };
      write(next);
      setParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.set("product", next.product);
          if (social) n.set("network", next.network);
          else n.set("channel", next.channel);
          return n;
        },
        { replace: true }
      );
    },
    [product, channel, network, social, setParams]
  );

  const search = social ? `?product=${product}&network=${network}` : `?product=${product}&channel=${channel}`;
  return useMemo(
    () => ({ product, channel, network, search, setProduct: (p) => apply({ product: p }), setChannel: (c) => apply({ channel: c }), setNetwork: (n) => apply({ network: n }) }),
    [product, channel, network, search, apply]
  );
}

/** The scope as a plain object, for building a link from anywhere (reads the remembered choice when the address has none). */
export function scopeSearch(pathname: string, current: URLSearchParams): string {
  const stored = read();
  const product = asProduct(current.get("product")) ?? stored.product ?? DEFAULT_PRODUCT;
  if (isSocialPath(pathname)) return `?product=${product}&network=${asNetwork(current.get("network")) ?? stored.network ?? DEFAULT_NETWORK}`;
  return `?product=${product}&channel=${asChannel(current.get("channel")) ?? stored.channel ?? DEFAULT_CHANNEL}`;
}
